import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useRender } from 'vue-email'

import VEmailFormatClientIntakeApprove from '@/components/common/VEmailFormatClientIntakeApprove.vue'
import VEmailFormatClientIntakeContracting from '@/components/common/VEmailFormatClientIntakeContracting.vue'
import VEmailFormatClientIntakeReject from '@/components/common/VEmailFormatClientIntakeReject.vue'
import VEmailFormatClientIntakeSubmit from '@/components/common/VEmailFormatClientIntakeSubmit.vue'
import { DataServiceFactory, EntraIDAuthService, NotificationService } from '@/factories'
import type { IClientIntake } from '@/interfaces'
import { Email } from '@/models'
import { EntraIDAuthConfig, MicrosoftGraphService, type User } from '@/services'
import { useAuthStore } from '@/stores'

const authService = DataServiceFactory.createAuthService('EntraIDAuth', EntraIDAuthConfig) as EntraIDAuthService
const notificationService = DataServiceFactory.createService('Notification', authService) as NotificationService
const microsoftGraphService = DataServiceFactory.createService('MicrosoftGraph', authService) as MicrosoftGraphService

const contractingEmail = import.meta.env.VITE_CONTRACTING_EMAIL
const sendToUser = import.meta.env.VITE_SEND_TO_USER
let environmentTitle = ''

export const useNotificationStore = defineStore('notification-store', () => {
	const getAuthStore = computed(() => {
		return useAuthStore()
	})

	async function getUserEmail(user: string): Promise<string> {
		let emailRecipient: string = ''
		if (sendToUser === 'true') {
			const emailRecipients: User[] = await microsoftGraphService.searchUsers(user)
			emailRecipient = emailRecipients[0].userPrincipalName as string
		} else {
			emailRecipient = 'nuevopeco@pinnacle-exp.com'
			environmentTitle = '[' + import.meta.env.VITE_ENV + ']'
		}
		return emailRecipient
	}

	async function sendClientIntakeSubmitEmail(user: string, url: string): Promise<string> {
		// Create a vue instance with email data
		const emailBody = await useRender(VEmailFormatClientIntakeSubmit, { props: { link: url } }, { pretty: true })
		const emailRecipient: string = await getUserEmail(user)

		const emailToSend = new Email(
			environmentTitle + ' Client Intake Submission',
			{
				body: { content: emailBody.html, content_type: 'HTML' },
				subject: environmentTitle + ' Client Intake Submission',
				to_recipients: [{ email_address: { address: emailRecipient } }]
			},
			new Date(),
			true,
			[emailRecipient],
			'nuevopeco@pinnacle-exp.com',
			''
		)
		return notificationService.sendClientIntakeEmail(emailToSend)
	}

	async function sendClientIntakeApproveEmail(
		user: string,
		clientName: string,
		cardCode: number | string
	): Promise<string> {
		// Create a vue instance with email data
		const emailBody = await useRender(
			VEmailFormatClientIntakeApprove,
			{ props: { clientName, cardCode } },
			{ pretty: true }
		)
		const emailRecipient: string = await getUserEmail(user)
		const emailToSend = new Email(
			environmentTitle + ' Client Intake Form Approval',
			{
				body: { content: emailBody.html, content_type: 'HTML' },
				subject: environmentTitle + ' Client Intake Form Approval',
				to_recipients: [{ email_address: { address: emailRecipient } }]
			},
			new Date(),
			true,
			[emailRecipient],
			'nuevopeco@pinnacle-exp.com',
			''
		)
		return notificationService.sendClientIntakeEmail(emailToSend)
	}

	async function sendClientIntakeRejectEmail(
		user: string,
		url?: string,
		reason?: string,
		clientName?: string
	): Promise<string> {
		// Create a vue instance with email data
		const emailBody = await useRender(
			VEmailFormatClientIntakeReject,
			{ props: { link: url, reason: reason, clientName: clientName } },
			{ pretty: true }
		)
		const emailRecipient: string = await getUserEmail(user)

		const emailToSend = new Email(
			environmentTitle + ' Notification of Rejected Client Intake Form',
			{
				body: { content: emailBody.html, content_type: 'HTML' },
				subject: environmentTitle + ' Notification of Rejected Client Intake Form',
				to_recipients: [{ email_address: { address: emailRecipient } }]
			},
			new Date(),
			true,
			[emailRecipient],
			'nuevopeco@pinnacle-exp.com',
			''
		)
		return notificationService.sendClientIntakeEmail(emailToSend)
	}

	async function sendClientIntakeContractingEmail(
		url: string,
		client: IClientIntake,
		clientList?: Array<IClientIntake>
	): Promise<string> {
		// Create a vue instance with email data
		const emailBody = await useRender(
			VEmailFormatClientIntakeContracting,
			{ props: { link: url, client: client, clientList: clientList } },
			{ pretty: true }
		)

		if (sendToUser === 'false') {
			environmentTitle = '[' + import.meta.env.VITE_ENV + ']'
		}

		const emailToSend = new Email(
			environmentTitle + ' New Client Form Awaiting Review',
			{
				body: { content: emailBody.html, content_type: 'HTML' },
				subject: environmentTitle + ' New Client Form Awaiting Review',
				to_recipients: [{ email_address: { address: contractingEmail } }]
			},
			new Date(),
			true,
			[contractingEmail],
			'nuevopeco@pinnacle-exp.com',
			''
		)

		return notificationService.sendClientIntakeEmail(emailToSend)
	}

	return {
		getAuthStore,
		sendClientIntakeSubmitEmail,
		sendClientIntakeApproveEmail,
		sendClientIntakeRejectEmail,
		sendClientIntakeContractingEmail
	}
})
